import type { ReactNode } from 'react';

import { Button } from 'cpool-components/input/Button';
import { ButtonVariant } from 'cpool-components/input/Button/types';
import { CurrencyInput } from 'cpool-components/input/CurrencyInput';
import { DateInput } from 'cpool-components/input/DateInput';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { LuPlus } from 'react-icons/lu';

import ItemDeleteButton from '../General/ItemDeleteButton.tsx';
import { FormField } from '~/features/leadDetails/components/Overview/CustomerData/shared/inputs/FormField';
import type { SchemaRealEstatesForm } from '~/features/leadDetails/schemas/realEstates.schema.ts';

type Props = {
    additionalAction?: ReactNode;
    realEstateIndex: number;
    disabled?: boolean;
};

const RealEstateLoans: React.FC<Props> = ({ additionalAction, disabled, realEstateIndex }) => {
    const { t } = useTranslation();
    const { control } = useFormContext<SchemaRealEstatesForm>();
    const { append, fields, remove } = useFieldArray<
        SchemaRealEstatesForm,
        `immobilien.${typeof realEstateIndex}.darlehen`,
        'darlehenId'
    >({
        control,
        keyName: 'darlehenId',
        name: `immobilien.${realEstateIndex}.darlehen`,
    });

    const removeRealEstateLoan = (index: number) => {
        remove(index);
    };

    const addRealEstateLoan = () => {
        append({
            rateMonatlich: null,
            restschuld: null,
            zinsbindungBis: null,
        });
    };

    return (
        <div className="space-y-2 pt-4">
            <div className="flex flex-wrap items-center gap-2">
                <Button
                    type="button"
                    variant={ButtonVariant.SECONDARY}
                    iconLeft={<LuPlus size={14} />}
                    onClick={addRealEstateLoan}
                    disabled={disabled}
                >
                    {t('REAL_ESTATE.BUTTONS.ADD_LOAN')}
                </Button>
                {additionalAction}
            </div>
            {fields.map((field, index) => (
                <div className="p-2.5 space-y-2" key={field.darlehenId}>
                    <div className="flex items-center justify-between gap-1">
                        <h6 className="text-sm font-bold text-midnight">
                            {t('REAL_ESTATE.LABELS.LOAN', { count: index + 1 })}
                        </h6>
                        <ItemDeleteButton
                            onClick={() => removeRealEstateLoan(index)}
                            translationPath="REAL_ESTATE.BUTTONS.DELETE_LOAN"
                            disabled={disabled}
                        />
                    </div>
                    <FormField<SchemaRealEstatesForm, number | null>
                        control={control}
                        name={`immobilien.${realEstateIndex}.darlehen.${index}.rateMonatlich`}
                    >
                        {({ error, onChange, value }) => (
                            <CurrencyInput
                                id={`immobilien.${realEstateIndex}.darlehen.${index}.rateMonatlich`}
                                label={t('REAL_ESTATE.LABELS.RATE')}
                                labelClassName="h-8 w-[175px] shrink-0 justify-start"
                                className="w-full"
                                value={value}
                                onChange={onChange}
                                min={0}
                                disabled={disabled}
                                error={error}
                            />
                        )}
                    </FormField>
                    <FormField<SchemaRealEstatesForm, number | null>
                        control={control}
                        name={`immobilien.${realEstateIndex}.darlehen.${index}.restschuld`}
                    >
                        {({ error, onChange, value }) => (
                            <CurrencyInput
                                id={`immobilien.${realEstateIndex}.darlehen.${index}.restschuld`}
                                label={t('REAL_ESTATE.LABELS.RESIDUAL_DEBT')}
                                labelClassName="h-8 w-[175px] shrink-0 justify-start"
                                className="w-full"
                                value={value}
                                onChange={onChange}
                                min={0}
                                disabled={disabled}
                                error={error}
                            />
                        )}
                    </FormField>
                    <FormField<SchemaRealEstatesForm, Date | null>
                        control={control}
                        name={`immobilien.${realEstateIndex}.darlehen.${index}.zinsbindungBis`}
                    >
                        {({ error, onChange, value }) => (
                            <DateInput
                                id={`immobilien.${realEstateIndex}.darlehen.${index}.zinsbindungBis`}
                                label={t('REAL_ESTATE.LABELS.FIXED_INTEREST_UNTIL')}
                                labelClassName="h-8 w-[175px] shrink-0 justify-start"
                                value={value}
                                onChange={onChange}
                                showDropdowns
                                disabled={disabled}
                                error={error}
                                autoComplete="off"
                                data-1p-ignore
                            />
                        )}
                    </FormField>
                </div>
            ))}
        </div>
    );
};

export default RealEstateLoans;
